import { useState, useEffect } from 'preact/hooks';
import { T } from '../styles/tokens';
import { Button, Textarea } from '../components/ui';

const BASE_URL_DEFAULT = '/api/v1/llm/v1';

interface TestResult {
  success: boolean;
  response?: string;
  model?: string;
  provider?: string;
  latencyMs?: number;
  tokens?: { prompt: number; completion: number; total: number };
  cost?: number;
  error?: string;
  raw?: any;
}

interface HistoryEntry {
  id: string;
  timestamp: string;
  baseUrl: string;
  model: string;
  message: string;
  result: TestResult;
}

const HISTORY_KEY = 'elon_llm_test_history';
function loadHistory(): HistoryEntry[] {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; }
}
function saveHistory(h: HistoryEntry[]) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(0, 100))); // max 100
}

export default function AdminLLMTest() {
  const [baseUrl, setBaseUrl] = useState(BASE_URL_DEFAULT);
  const [token, setToken] = useState('');
  const [models, setModels] = useState<{ id: string; name?: string; owned_by?: string; context_window?: number; object?: string; created?: number }[]>([]);
  const [showModelsTable, setShowModelsTable] = useState(true);
  const [selectedModel, setSelectedModel] = useState('');
  const [message, setMessage] = useState('Diga oi em 3 palavras');
  const [loading, setLoading] = useState(false);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { setHistory(loadHistory()); }, []);

  const fetchModels = async () => {
    if (!token) return;
    setFetchingModels(true);
    setModels([]);
    try {
      const res = await fetch(`${baseUrl}/models`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      const list = data?.data || data?.models || [];
      setModels(list);
      if (list.length > 0 && !selectedModel) setSelectedModel(list[0].id);
    } catch (e: any) {
      setModels([]);
    }
    setFetchingModels(false);
  };

  const runTest = async () => {
    if (!token || !selectedModel || !message) return;
    setLoading(true);
    setResult(null);
    const start = Date.now();
    try {
      const res = await fetch(`${baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: [{ role: 'user', content: message }],
          max_tokens: 200,
        }),
      });
      const latencyMs = Date.now() - start;
      const data = await res.json();

      if (!res.ok || data.error) {
        setResult({
          success: false,
          error: data.error?.message || data.error?.type || JSON.stringify(data.error) || `HTTP ${res.status}`,
          latencyMs,
          raw: data,
        });
      } else {
        const content = data.choices?.[0]?.message?.content || '';
        const usage = data.usage || {};
        const router = data._elon_router || {};
        setResult({
          success: true,
          response: content,
          model: data.model || selectedModel,
          provider: router.provider || data.provider || '—',
          latencyMs,
          tokens: {
            prompt: usage.prompt_tokens || 0,
            completion: usage.completion_tokens || 0,
            total: usage.total_tokens || (usage.prompt_tokens || 0) + (usage.completion_tokens || 0),
          },
          cost: typeof router.price_charged === 'number' ? router.price_charged : (typeof router.cost_provider === 'number' ? router.cost_provider : 0),
          raw: data,
        });
      }
    } catch (e: any) {
      setResult({
        success: false,
        error: e.message || 'Request failed',
        latencyMs: Date.now() - start,
      });
    }
    setLoading(false);
  };

  // Save to history whenever result changes
  useEffect(() => {
    if (!result) return;
    const entry: HistoryEntry = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      timestamp: new Date().toISOString(),
      baseUrl,
      model: result.model || selectedModel,
      message,
      result,
    };
    const updated = [entry, ...history].slice(0, 100);
    setHistory(updated);
    saveHistory(updated);
  }, [result]);

  const S = {
    page: { maxWidth: 900, margin: '0 auto', padding: '32px 24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' } as const,
    h1: { fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 6 } as const,
    subtitle: { fontSize: 14, color: T.textMuted, marginBottom: 24 } as const,
    card: { background: T.bgInput, borderRadius: 12, border: `1px solid ${T.border}`, padding: 20, marginBottom: 16 } as const,
    label: { fontSize: 12, fontWeight: 600, color: T.textSecondary, display: 'block' as const, marginBottom: 6, textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    input: { width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.bgHover}`, background: T.bgCard, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'monospace' },
    select: { width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.bgHover}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const },
    btn: (variant: 'primary' | 'secondary') => ({
      padding: '10px 20px', borderRadius: 10, border: variant === 'primary' ? 'none' : `1px solid ${T.borderHover}`,
      background: variant === 'primary' ? T.primary : 'transparent',
      color: variant === 'primary' ? '#fff' : T.textSecondary,
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
    }),
    row: { display: 'flex', gap: 12, alignItems: 'flex-end' } as const,
    stat: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', padding: '12px 16px', background: T.bgSubtle, borderRadius: 10, flex: 1, minWidth: 100 },
    statLabel: { fontSize: 11, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.04em', marginBottom: 4 },
    statValue: { fontSize: 18, fontWeight: 700, color: T.text },
  };

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Teste LLM</h1>
      <p style={S.subtitle}>Teste a API do Elon Router. Configure URL, token e modelo, depois envie uma mensagem de teste.</p>

      {/* Config Card */}
      <div style={S.card}>
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>URL da API</label>
          <input style={S.input} value={baseUrl} onInput={(e: any) => setBaseUrl(e.target.value)} placeholder={BASE_URL_DEFAULT} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Token (Bearer)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={{ ...S.input, flex: 1 }}
              type={showToken ? 'text' : 'password'}
              value={token}
              onInput={(e: any) => setToken(e.target.value)}
              placeholder="ert_live_..."
            />
            <Button variant="secondary" onClick={() => setShowToken(!showToken)}>
              {showToken ? 'Ocultar' : 'Mostrar'}
            </Button>
            <Button variant="primary" onClick={fetchModels} disabled={fetchingModels || !token} loading={fetchingModels}>
              {fetchingModels ? 'Buscando...' : 'Buscar Modelos'}
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Modelo</label>
          <select style={S.select} value={selectedModel} onChange={(e: any) => setSelectedModel(e.target.value)}>
            {models.length === 0 && <option value="">— Busque os modelos primeiro —</option>}
            {models.map(m => (
              <option key={m.id} value={m.id}>{m.name || m.id}{m.owned_by ? ` (${m.owned_by})` : ''}</option>
            ))}
          </select>
          {models.length > 0 && (
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{models.length} modelos disponiveis</div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Mensagem de Teste</label>
          <Textarea
            style={{ ...S.input, fontFamily: 'inherit', minHeight: 60, resize: 'vertical' as const }}
            value={message}
            onInput={(e: any) => setMessage(e.target.value)}
          />
        </div>

        <Button
          variant="primary"
          fullWidth
          onClick={runTest}
          disabled={loading || !token || !selectedModel || !message}
          loading={loading}
        >
          {loading ? 'Testando...' : 'Testar'}
        </Button>
      </div>

      {/* Models Table */}
      {models.length > 0 && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Modelos Disponiveis</span>
              <span style={{ fontSize: 12, color: T.textMuted, marginLeft: 8 }}>{models.length} modelos</span>
            </div>
            <Button variant="secondary" onClick={() => setShowModelsTable(!showModelsTable)}>
              {showModelsTable ? 'Ocultar' : 'Mostrar'}
            </Button>
          </div>
          {showModelsTable && (
            <div style={{ overflowX: 'auto' as const }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.bgHover}` }}>
                    {['#', 'ID (usar na API)', 'Nome', 'Provedor', 'Contexto', 'Tipo'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const, fontWeight: 600, letterSpacing: '0.04em', whiteSpace: 'nowrap' as const }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {models.map((m, i) => (
                    <tr key={m.id} style={{ borderBottom: `1px solid ${T.bgCard}`, cursor: 'pointer', background: selectedModel === m.id ? T.primaryBg : 'transparent' }}
                      onClick={() => setSelectedModel(m.id)}>
                      <td style={{ padding: '8px 12px', color: T.textMuted }}>{i + 1}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: selectedModel === m.id ? T.primary : T.text, fontWeight: selectedModel === m.id ? 600 : 400 }}>{m.id}</td>
                      <td style={{ padding: '8px 12px', color: T.textSecondary }}>{m.name || m.id}</td>
                      <td style={{ padding: '8px 12px', color: T.textSecondary }}>{m.owned_by || '—'}</td>
                      <td style={{ padding: '8px 12px', color: T.textSecondary }}>{m.context_window ? `${(m.context_window / 1000).toFixed(0)}K` : '—'}</td>
                      <td style={{ padding: '8px 12px', color: T.textMuted }}>{m.object || 'model'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Result Card */}
      {result && (
        <div style={{ ...S.card, borderColor: result.success ? T.successBg : T.dangerBg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: result.success ? T.success : T.danger }}>
              {result.success ? 'Sucesso' : 'Erro'}
            </span>
            <span style={{
              display: 'inline-block', padding: '2px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
              background: result.success ? T.successBg : T.dangerBg,
              color: result.success ? T.success : T.danger,
            }}>
              {result.latencyMs}ms
            </span>
          </div>

          {result.success ? (
            <>
              {/* Stats */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' as const }}>
                <div style={S.stat}>
                  <span style={S.statLabel}>Modelo</span>
                  <span style={{ ...S.statValue, fontSize: 13 }}>{result.model}</span>
                </div>
                <div style={S.stat}>
                  <span style={S.statLabel}>Provedor</span>
                  <span style={{ ...S.statValue, fontSize: 13 }}>{result.provider}</span>
                </div>
                <div style={S.stat}>
                  <span style={S.statLabel}>Tokens</span>
                  <span style={S.statValue}>{result.tokens?.total || 0}</span>
                </div>
                <div style={S.stat}>
                  <span style={S.statLabel}>Custo</span>
                  <span style={{ ...S.statValue, color: T.warning }}>${(typeof result.cost === 'number' ? result.cost : 0).toFixed(6)}</span>
                </div>
                <div style={S.stat}>
                  <span style={S.statLabel}>Latencia</span>
                  <span style={S.statValue}>{result.latencyMs}ms</span>
                </div>
              </div>

              {/* Response */}
              <div>
                <label style={S.label}>Resposta</label>
                <div style={{ background: T.bg, borderRadius: 10, padding: 16, fontSize: 14, color: T.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' as const }}>
                  {result.response}
                </div>
              </div>

              {/* Token breakdown */}
              {result.tokens && (
                <div style={{ marginTop: 12, fontSize: 12, color: T.textMuted }}>
                  Prompt: {result.tokens.prompt} | Completion: {result.tokens.completion} | Total: {result.tokens.total}
                </div>
              )}
            </>
          ) : (
            <div style={{ background: T.dangerBg, borderRadius: 10, padding: 16, fontSize: 13, color: T.danger, lineHeight: 1.6 }}>
              {result.error}
            </div>
          )}

          {/* Raw JSON toggle */}
          {result.raw && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ fontSize: 12, color: T.textMuted, cursor: 'pointer' }}>Resposta completa (JSON)</summary>
              <pre style={{ background: T.bg, borderRadius: 10, padding: 12, fontSize: 11, color: T.textSecondary, overflow: 'auto', maxHeight: 300, marginTop: 8, whiteSpace: 'pre-wrap' as const }}>
                {JSON.stringify(result.raw, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* History */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showHistory ? 16 : 0 }}>
          <div style={{ cursor: 'pointer' }} onClick={() => setShowHistory(!showHistory)}>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Historico de Testes</span>
            <span style={{ fontSize: 12, color: T.textMuted, marginLeft: 8 }}>{history.length} testes</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {history.length > 0 && (
              <Button variant="secondary" size="xs" onClick={() => {
                if (confirm('Limpar todo o historico?')) { setHistory([]); saveHistory([]); }
              }}>Limpar</Button>
            )}
            <Button variant="secondary" onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? 'Ocultar' : 'Mostrar'}
            </Button>
          </div>
        </div>
        {showHistory && history.length > 0 && (
          <div style={{ overflowX: 'auto' as const }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.bgHover}` }}>
                  {['Data/Hora', 'URL', 'Modelo', 'Status', 'Latencia', 'Tokens', 'Custo', 'Mensagem', 'Resposta'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: T.textMuted, fontSize: 10, textTransform: 'uppercase' as const, fontWeight: 600, letterSpacing: '0.04em', whiteSpace: 'nowrap' as const }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map(h => {
                  const r = h.result;
                  const date = new Date(h.timestamp);
                  return (
                    <tr key={h.id} style={{ borderBottom: `1px solid ${T.bgCard}` }}>
                      <td style={{ padding: '8px 10px', color: T.textSecondary, whiteSpace: 'nowrap' as const, fontSize: 11 }}>
                        {date.toLocaleDateString('pt-BR')} {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </td>
                      <td style={{ padding: '8px 10px', color: T.textMuted, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, fontSize: 10, fontFamily: 'monospace' }} title={h.baseUrl || ''}>
                        {(h.baseUrl || '').replace('/api/v1/', '').replace('https://', '')}
                      </td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: T.text, fontSize: 11 }}>{r.model || h.model}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                          background: r.success ? T.successBg : T.dangerBg,
                          color: r.success ? T.success : T.danger,
                        }}>{r.success ? 'OK' : 'ERRO'}</span>
                      </td>
                      <td style={{ padding: '8px 10px', color: T.textSecondary, fontFamily: 'monospace', fontSize: 11 }}>{r.latencyMs || 0}ms</td>
                      <td style={{ padding: '8px 10px', color: T.textSecondary, fontSize: 11 }}>
                        {r.tokens ? `${r.tokens.prompt}/${r.tokens.completion}/${r.tokens.total}` : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', color: T.warning, fontFamily: 'monospace', fontSize: 11 }}>
                        {typeof r.cost === 'number' ? `$${r.cost.toFixed(6)}` : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', color: T.textMuted, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, fontSize: 11 }} title={h.message}>{h.message}</td>
                      <td style={{ padding: '8px 10px', color: T.textSecondary, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, fontSize: 11 }} title={r.response || r.error || ''}>
                        {r.success ? (r.response || '').slice(0, 80) : (r.error || '').slice(0, 80)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {showHistory && history.length === 0 && (
          <div style={{ textAlign: 'center' as const, padding: 24, color: T.textMuted, fontSize: 13 }}>Nenhum teste realizado ainda.</div>
        )}
      </div>
    </div>
  );
}
